// @flow

import { useEffect, useCallback, useRef } from 'react';
import * as AvailableMetricsModel from '../../../models/available-metrics';
import * as WidgetPropsModel from '../../../models/widget-props';
import WidgetUI from '../../widgets-ui/chart';
import { buildWidgetDateString } from '../common/stringHelpers';
import { useHasWidgetChanged, useSafeState, useSafeVisible } from '../common/hooks';
import { processChartWidget } from '@auravisionlabs/aura-pkg-report-widgets/dist/core/widgets/chart';
import { browserQueryAPI } from '../../../services/query-api-browser';
import { useWidgetRenderState } from '../../../hooks/use-widget-render-state';

const ChartWidget = ({
  widget,
  availableMetrics,
  uiOptions = {},
  reportWidgetId,
}: WidgetPropsModel.chartT) => {
  const [loading, setLoading] = useSafeState(false);
  const [progress, setProgress] = useSafeState(0);
  const [error, setError] = useSafeState(false);
  const [autoTitle, setAutoTitle] = useSafeState('');
  const [warningMessage, setWarningMessage] = useSafeState('');
  const [metricsForUI, setMetricsForUI] = useSafeState([]);
  const [chartData, setChartData] = useSafeState([]);
  const [fetchRequired, setFetchRequired] = useSafeState(true);
  const hasInitialFetchedRef = useRef(false);
  const hasWidgetChanged = useHasWidgetChanged(widget);
  const [currentElement, isVisible] = useSafeVisible((vi) => uiOptions.disableViewportLoading || vi > 0);
  const { reportLoading, reportComplete, reportError } = useWidgetRenderState(reportWidgetId);

  const title = widget.customTitle || autoTitle;

  const loadWidgetData = useCallback(async () => {
    setLoading(true);
    setError(false);
    setProgress(0);
    setWarningMessage('');
    reportLoading();

    try {
      const config = {
        metrics: widget.metrics,
        locations: widget.locations,
        period: widget.period,
        demographicFilter: widget.demographicFilter,
        aggregation: widget.aggregation,
        ...(widget.customTitle && { customTitle: widget.customTitle }),
      };

      const deps = {
        queryAPI: browserQueryAPI,
        availableMetrics: availableMetrics,
        locations: [],
        startOfWeek: 'monday',
        onProgress: (completed, total) => setProgress(completed / total),
      };

      const widgetData = await processChartWidget(config, deps);

      setAutoTitle(widgetData.title);

      // Capture error/warning message from package
      if (widgetData.error) {
        setWarningMessage(widgetData.error);
      }

      // Extract metrics and chart data from widgetData
      const metrics = widgetData.columns.slice(1).map((col) => {
        // Extract the metric index from the dataKey suffix
        // Package creates dataKeys like "metricKey-0", "metricKey-1"
        // This handles cases where multiple metrics share the same metricKey (e.g., with different filters)
        const indexMatch = col.key.match(/-(\d+)$/);
        const metricIndex = indexMatch ? parseInt(indexMatch[1], 10) : 0;
        const originalMetric = widget.metrics[metricIndex];

        return {
          label: col.header,
          dataKey: col.key,
          unitType: col.defaultUnit,
          description: '',
          chartDisplayType: originalMetric?.chartDisplayType,
        };
      });

      const chartDataFromRows = widgetData.rows.map((row) => {
        const dataPoint = { index: row[0].value };
        metrics.forEach((metric, idx) => {
          dataPoint[metric.dataKey] = row[idx + 1].value;
        });
        return dataPoint;
      });

      setMetricsForUI(metrics);
      setChartData(chartDataFromRows);

      // Set error state only if all metrics failed
      if (metrics.length === 0 && widget.metrics.length > 0) {
        setError(true);
        reportError('All metrics failed to load');
      } else {
        // Report completion to context - use package output directly
        reportComplete(widgetData);
      }
    } catch (err) {
      console.error('Error loading Chart widget:', err);
      setError(true);
      reportError(err.message || 'Failed to load Chart widget');
    } finally {
      setLoading(false);
    }
  }, [widget, availableMetrics, widget.customTitle]);

  useEffect(() => {
    if (hasWidgetChanged && hasInitialFetchedRef.current) setFetchRequired(true);
  }, [hasWidgetChanged]);

  useEffect(() => {
    if (fetchRequired && isVisible) {
      setFetchRequired(false);
      hasInitialFetchedRef.current = true;
      loadWidgetData();
    }
  }, [fetchRequired, isVisible, loadWidgetData]);

  const subtitle = buildWidgetDateString(widget, true);

  return (
    <div ref={currentElement}>
      <WidgetUI
        errors={error}
        loading={loading}
        progress={progress}
        title={title}
        subtitle={subtitle}
        metrics={metricsForUI}
        chartData={chartData}
        aggregation={widget.aggregation}
      />
    </div>
  );
};

export default ChartWidget;
